import React from 'react';
import { createRoot, Root as ReactRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import Root from './Root';
import reportWebVitals from './reportWebVitals';
import i18n from './i18n/i18n';
import { getDefaultLogger } from './components/logger/logger';
import { ClientConfig, ClientConfigContext } from './utils/clientConfigContext';
import { VeriffSessionProvider } from './utils/veriffSessionContext';
import { VerificationStatus } from './@types/VerificationStatus';
import { VerificationMethod } from './@types/VerificationMethod';
import { VerificationCompletionData } from './@types/VerificationCompletionData';

const log = getDefaultLogger();

export interface TVSConfig {
  clientId: string;
  baseUrl?: string;
  verificationMethods?: VerificationMethod[];
  hideToasterOnSuccess?: boolean;
  showSessionIdInErrorMessage?: boolean;
  userFirstName?: string;
  userLastName?: string;
  nameEditable?: boolean;
  nameCheckEnableForVeriff?: boolean;
  onVerificationComplete?: (status: VerificationStatus, data: VerificationCompletionData) => void;
  userId?: string;
  isDarkModeSupported?: boolean;
}

window.TVSModal = class {
  private root: ReactRoot | null = null;
  private readonly tvsContainer: HTMLElement | null;
  private readonly config: ClientConfig;

  constructor(containerId: string, config: TVSConfig) {
    this.tvsContainer = document.getElementById(containerId);

    this.config = {
      clientId: config.clientId,
      baseUrl: this.cleanBaseUrl(config.baseUrl || window.location.href),
      verificationMethods: config.verificationMethods,
      hideToasterOnSuccess: config.hideToasterOnSuccess,
      userFirstName: config.userFirstName,
      userLastName: config.userLastName,
      nameEditable: config.nameEditable,
      nameCheckEnableForVeriff: config.nameCheckEnableForVeriff,
      userId: config.userId,
      isDarkModeSupported: config.isDarkModeSupported,
    };

    if (!this.tvsContainer) {
      log.warn('Could not find root element to render TVS widget');
      return;
    }

    log.info(`Initializing TVSModal with clientId: ${this.config.clientId} and baseUrl: ${this.config.baseUrl}`);

    this.root = createRoot(this.tvsContainer);
    this.root.render(
      <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <I18nextProvider i18n={i18n}>
          <ClientConfigContext.Provider value={this.config}>
            <VeriffSessionProvider>
              <Root onVerificationComplete={config.onVerificationComplete ?? (() => {})} />
            </VeriffSessionProvider>
          </ClientConfigContext.Provider>
        </I18nextProvider>
      </BrowserRouter>,
    );
  }

  private cleanBaseUrl = (url: string) => {
    const urlObj = new URL(url);
    urlObj.searchParams.delete('tvs_state');
    return urlObj.toString();
  };

  public triggerVerification() {
    const url = new URL(window.location.href);
    url.searchParams.set('tvs_state', 'verification_landing_page');
    window.history.pushState({}, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  public destroy() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }

    if (this.tvsContainer) {
      this.tvsContainer.innerHTML = '';
    }

    const url = new URL(window.location.href);
    if (url.searchParams.has('tvs_state')) {
      url.searchParams.delete('tvs_state');
      window.history.replaceState({}, '', url);
    }
  }
};

reportWebVitals();
